import { API_BASE } from '../lib/config';
import './shared/content-design.css';
/**
 * Trainova Layout - Context-Based Navigation
 *
 * Three distinct contexts that never mix:
 * 1. LAG (Team) - Daily work, team functions only
 * 2. FÖRENING (Organization) - Organization management
 * 3. PLATTFORM (Platform) - Trainova admin (app_admin only)
 *
 * Principle: "Admin är inte en plats – det är en roll i en kontext"
 */
import React, {
  useState,
  useEffect,
  createContext,
  useContext,
  useMemo,
  useCallback
} from 'react';
import {
  NavLink,
  Link,
  useNavigate,
  useLocation
} from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { clubSections, clubSectionForLocation } from './org/clubNavigation';
import { useParent } from '../context/ParentContext';
import { useChatView } from '../context/ChatViewContext';
import {
  availableTeams,
  accountTeamRoleLabel,
  hasOwnTeamWorkspace,
} from '../lib/accountContexts';
import { useMobileContext } from '../context/MobileContext';
import { useModules } from '../context/ModulesContext';
import { useWebSocketContext } from '../context/WebSocketContext';
import { cn } from '../lib/utils';
import { isExerciseDetailRoute } from '../lib/drillPresentation';
import {
  getUnreadPraiseCount,
  getTotalUnreadCount,
  getUserContext
} from '../lib/api';
import {
  Calendar,
  Users,
  Dumbbell,
  Trophy,
  MessageSquare,
  BarChart3,
  Star,
  FileVideo,
  Settings,
  LogOut,
  Menu,
  ChevronDown,
  Check,
  ClipboardList,
  Heart,
  HelpCircle,
  Home,
  FileText,
  Building2,
  Layers,
  CreditCard,
  FileBox,
  LayoutDashboard,
  Headphones,
  ChevronRight,
  ArrowLeft,
  Key,
  FileQuestion,
  Target,
  TrendingUp,
  Activity,
  Shield,
  User,
  Plus
} from 'lucide-react';
import { Button } from './ui/button';
import {
  Avatar,
  AvatarFallback,
  AvatarImage
} from './ui/avatar';
import {
  Sheet,
  SheetContent,
  SheetTrigger
} from './ui/sheet';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from './ui/dropdown-menu';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import TrialWarningBanner from './TrialWarningBanner';
import SupportModeBanner from './SupportModeBanner';
import { MobileBottomNav } from './mobile/MobileBottomNav';
import MobileAppHeader from './mobile/MobileAppHeader';
import NotificationBell from './notifications/NotificationBell';
import ChildSwitcher from './parent/ChildSwitcher';

const API = API_BASE;

// =============================================================================
// CONTEXT TYPES
// =============================================================================

const NavigationContext = createContext(null);

export const useNavigationContext = () =>
  useContext(NavigationContext);

// Context types
const CONTEXTS = {
  TEAM: 'team',
  ORGANIZATION: 'organization',
  PLATFORM: 'platform'
};

// =============================================================================
// MENU STRUCTURES BY CONTEXT
// =============================================================================

// LAG (Team) Context Menu – Academy Pro sidebar structure
// moduleKey: If set, item is hidden when that module is disabled
// `label` values are i18n keys resolved at render time via useTranslation.
const teamMenuSections = [
  {
    id: 'main',
    label: 'navigation:sections.main',
    items: [
      {
        to: '/oversikt',
        icon: Home,
        label: 'navigation:items.overview'
      },
      {
        to: '/calendar',
        icon: Calendar,
        label: 'navigation:items.calendar',
        moduleKey: 'calendar'
      },
      {
        to: '/training',
        icon: Dumbbell,
        label: 'navigation:items.training',
        moduleKey: 'training'
      },
      {
        to: '/matches',
        icon: Trophy,
        label: 'navigation:items.matches',
        moduleKey: 'match'
      },
      {
        to: '/tactics/board',
        icon: Target,
        label: 'navigation:items.board',
        moduleKey: 'tactic'
      },
      {
        to: '/team',
        icon: Users,
        label: 'navigation:items.squad',
        moduleKey: 'squad'
      }
    ]
  },
  {
    id: 'development',
    label: 'navigation:sections.development',
    items: [
      {
        to: '/development',
        icon: TrendingUp,
        label: 'Utveckling',
        participantOnly: true,
        moduleKey: 'player_development'
      },
      {
        to: '/team/development',
        icon: TrendingUp,
        label: 'Spelarutveckling',
        coachOnly: true,
        moduleKey: 'player_development'
      },
      {
        to: '/utveckling/individuell-traning',
        icon: Target,
        label: 'navigation:items.individual_training',
        coachOnly: true,
        moduleKey: 'individual_training'
      },
      {
        to: '/development/evaluations',
        icon: ClipboardList,
        label: 'navigation:items.evaluations',
        moduleKey: 'evaluations'
      },
      {
        to: '/development/assessments',
        icon: Star,
        label: 'navigation:items.assessments',
        coachOnly: true,
        moduleKey: 'ratings'
      },
      {
        to: '/development/praise',
        icon: Heart,
        label: 'navigation:items.praise',
        moduleKey: 'praise'
      },
      {
        to: '/development/challenges',
        icon: Trophy,
        label: 'navigation:items.challenges',
        moduleKey: 'challenges'
      },
      {
        to: '/development/tests',
        icon: Activity,
        label: 'navigation:items.tests',
        coachOnly: true,
        moduleKey: 'tests'
      },
      {
        to: '/development/statistics',
        icon: BarChart3,
        label: 'navigation:items.statistics',
        moduleKey: 'statistics'
      }
    ]
  },
  {
    id: 'communication',
    label: 'navigation:sections.communication',
    items: [
      {
        to: '/chat',
        icon: MessageSquare,
        label: 'navigation:items.chat',
        moduleKey: 'chat'
      },
      {
        to: '/posts',
        icon: FileText,
        label: 'navigation:items.posts',
        coachOnly: true,
        moduleKey: 'posts'
      }
    ]
  },
  {
    id: 'system',
    label: 'navigation:sections.system',
    items: [
      {
        to: '/media',
        icon: FileVideo,
        label: 'navigation:items.media',
        moduleKey: 'media'
      },
      {
        to: '/team/settings',
        icon: Settings,
        label: 'navigation:items.team_settings',
        coachOnly: true
      },
      {
        to: '/settings',
        icon: Settings,
        label: 'navigation:items.my_profile'
      }
    ]
  }
];

// FÖRÄLDER (Parent) Team Menu — passive observer view
// Mirrors mobile structure: Hem, Kalender, Trupp, Utveckling, Match, Media, Chatt, Profil, Support
const parentTeamMenuSections = [
  {
    id: 'main',
    label: 'navigation:sections.main',
    items: [
      {
        to: '/oversikt',
        icon: Home,
        label: 'navigation:items.overview'
      },
      {
        to: '/min-traning',
        icon: Target,
        label: 'navigation:items.my_training',
        playerOrParent: true,
        moduleKey: 'individual_training'
      },
      {
        to: '/calendar',
        icon: Calendar,
        label: 'navigation:items.calendar',
        moduleKey: 'calendar'
      },
      {
        to: '/matches',
        icon: Trophy,
        label: 'navigation:items.matches',
        moduleKey: 'match'
      },
      {
        to: '/team',
        icon: Users,
        label: 'navigation:items.squad',
        moduleKey: 'squad'
      },
      {
        to: '/development',
        icon: TrendingUp,
        label: 'navigation:items.development_area',
        moduleKey: 'player_development'
      }
    ]
  },
  {
    id: 'communication',
    label: 'navigation:sections.communication',
    items: [
      {
        to: '/chat',
        icon: MessageSquare,
        label: 'navigation:items.chat',
        moduleKey: 'chat'
      },
      {
        to: '/media',
        icon: FileVideo,
        label: 'navigation:items.media',
        moduleKey: 'media'
      }
    ]
  },
  {
    id: 'account',
    label: 'navigation:sections.account',
    items: [
      {
        to: '/settings',
        icon: User,
        label: 'navigation:items.profile'
      },
      {
        to: '/feedback',
        icon: HelpCircle,
        label: 'navigation:items.support'
      }
    ]
  }
];

// FÖRENING (Organization) Context Menu
//
// Org Admin IA (Feb 2026) — only routes that actually exist may live here.
// Pages `/org/:orgId/{fees,invoices,documents,settings}` are NOT mounted,
// so they were removed instead of leaving them as broken 404 links.
const clubIcons = { overview: LayoutDashboard, identity: Building2, teams: Layers, people: Users, development: TrendingUp, planning: Calendar, administration: Shield };
const organizationMenuSections = [{
  id: 'org_overview',
  items: clubSections.map(section => ({
    to: '/org/{orgId}' + section.path,
    icon: clubIcons[section.id],
    clubSection: section.id,
    clubLabel: section.label,
  }))
}];

// PLATTFORM (Platform) Context Menu - App Admin only
// Single canonical Platform Admin navigation. Only real, working destinations.
// Subscriptions/Invoices removed (subscription truth lives in Customer 360;
// invoices are Stripe-owned) until real pages exist.
const platformMenuSections = [
  {
    id: 'platform_overview',
    label: 'navigation:sections.overview',
    items: [
      {
        to: '/platform-admin',
        icon: LayoutDashboard,
        label: 'navigation:items.dashboard'
      }
    ]
  },
  {
    id: 'platform_customers',
    label: 'navigation:sections.customers',
    items: [
      {
        to: '/platform-admin/customers',
        icon: Users,
        label: 'navigation:items.customers'
      },
      {
        to: '/platform-admin/orgs',
        icon: Building2,
        label: 'navigation:items.organizations'
      }
    ]
  },
  {
    id: 'platform_business',
    label: 'navigation:sections.business',
    items: [
      { to: '/platform-admin/exercises', icon: ClipboardList, clubLabel: ['Övningsbank', 'Exercise library'] },
      {
        to: '/platform-admin/requests',
        icon: FileQuestion,
        label: 'navigation:items.requests',
        badge: 'pending_requests'
      }
    ]
  },
  {
    id: 'platform_service',
    label: 'navigation:sections.service',
    items: [
      {
        to: '/platform-admin/support',
        icon: Headphones,
        label: 'navigation:items.support'
      }
    ]
  }
];

// =============================================================================
// NAV ITEM COMPONENT
// =============================================================================

const NavItem = ({
  to,
  icon: Icon,
  label,
  onClick,
  badge,
  end = false,
  clubSection
}) => {
  const { pathname, search } = useLocation();
  const developmentConversation = pathname === '/development/conversations' || pathname.startsWith('/development/conversations/');
  if (clubSection) {
    const active = (/^\/org\/[^/]+\/exercises\/[^/]+\/edit\/?$/.test(pathname) ? 'planning' : clubSectionForLocation(pathname, search)) === clubSection;
    return <Link to={to} onClick={onClick} aria-current={active ? 'page' : undefined}
      className={cn('tn-desktop-nav-link flex items-center h-[39px] gap-[10px] px-[11px] rounded-[9px] transition-all duration-200 relative', active
        ? 'bg-[rgb(var(--tn-bg-181c22))] text-[var(--t-accent)] font-medium before:absolute before:left-0 before:top-[8px] before:bottom-[8px] before:w-[2px] before:rounded-full before:bg-[var(--tn-accent-fill)]'
        : 'text-[rgb(var(--tn-text-a8b0bb))] hover:bg-[rgb(var(--tn-bg-171b21))] hover:text-[rgb(var(--tn-text-f2f4f7))]')}>
      <Icon className="w-[17px] h-[17px] flex-shrink-0" /><span className="text-[13px] font-medium truncate">{label}</span>
    </Link>;
  }

  return (
  <NavLink
    to={to}
    end={end || (to === '/tactics' && pathname === '/tactics/board') || to === '/team' || (to === '/development' && !developmentConversation)}
    onClick={onClick}
    className={({ isActive }) =>
      cn(
        'tn-desktop-nav-link flex items-center h-[39px] gap-[10px] px-[11px] rounded-[9px] transition-all duration-200 relative',
        (isActive || (to === '/matches' && (pathname === '/tactics/new' || /^\/tactics\/[^/]+\/edit$/.test(pathname))))
          ? "bg-[rgb(var(--tn-bg-181c22))] text-[var(--t-accent)] font-medium before:absolute before:left-0 before:top-[8px] before:bottom-[8px] before:w-[2px] before:rounded-full before:bg-[var(--tn-accent-fill)]"
          : "text-[rgb(var(--tn-text-a8b0bb))] hover:bg-[rgb(var(--tn-bg-171b21))] hover:text-[rgb(var(--tn-text-f2f4f7))]" 
      )
    }
  >
    <Icon className="w-[17px] h-[17px] flex-shrink-0" />

    <span className="text-[13px] font-medium truncate">
      {label}
    </span>

    {badge > 0 && (
      <Badge className="absolute right-2 h-5 min-w-5 px-1.5 text-[10px] bg-destructive text-[rgb(var(--tn-text-ffffff))]">
        {badge > 99 ? '99+' : badge}
      </Badge>
    )}
  </NavLink>
  );
};

// Section header
const SectionHeader = ({ label }) => (
  <div className="tn-sidebar-section px-[11px] py-[5px] mt-[13px] first:mt-0">
    <span className="text-[10px] font-semibold text-[var(--t-text-secondary)]/60 uppercase tracking-wider">
      {label}
    </span>
  </div>
);

// Organization navigation follows the opened URL, including direct app-admin links.
// Never substitute the first membership: it may belong to a different club.
const organizationIdFromPath = pathname => {
  const match = String(pathname || '').match(/^\/org\/([^/]+)(?:\/|$)/);
  if (!match) return null;
  try {
    const id = decodeURIComponent(match[1]);
    return id && !/[\/\\{}\s]/.test(id) ? id : null;
  } catch {
    return null;
  }
};

const organizationMenuForPath = (sections, pathname) => {
  const orgId = organizationIdFromPath(pathname);
  if (!orgId) return [];
  return sections.map(section => ({
    ...section,
    items: section.items.map(item => ({
      ...item,
      to: item.to.replace('{orgId}', encodeURIComponent(orgId))
    }))
  }));
};

// =============================================================================
// CONTEXT SWITCHER COMPONENT
// =============================================================================

const ContextSwitcher = ({
  currentContext,
  setCurrentContext,
  activeTeam,
  allTeams,
  onTeamChange,
  onRefreshTeams,
  userOrganizations,
  isOrgAdmin,
  isAppAdmin,
  isCoach,
  isMobile
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { exitSupportMode, user, teamSwitching } = useAuth();
  const [selecting, setSelecting] = useState(false);
  const switchableTeams = availableTeams(allTeams);
  const [refreshing, setRefreshing] =
    React.useState(false);

  const handleManualRefresh = async () => {
    if (!onRefreshTeams) return;

    setRefreshing(true);

    try {
      await onRefreshTeams();
    } finally {
      setRefreshing(false);
    }
  };

  // Extract current org ID from URL when in organization context
  const activeOrgId =
    currentContext === CONTEXTS.ORGANIZATION
      ? organizationIdFromPath(location.pathname)
      : null;

  // Find the active organization object
  const activeOrg = activeOrgId
    ? userOrganizations?.find(
        org => org.id === activeOrgId
      )
    : null;

  const handleContextSwitch = (
    context,
    orgId = null
  ) => {
    if (teamSwitching || selecting) return;
    // Leaving the team workspace always clears Platform Admin support mode.
    exitSupportMode();
    setCurrentContext(context);

    if (context === CONTEXTS.TEAM) {
      // Unified academy overview for all roles & devices
      navigate('/oversikt', {
        replace: true
      });
    } else if (
      context === CONTEXTS.ORGANIZATION &&
      orgId
    ) {
      navigate(`/org/${encodeURIComponent(orgId)}`, {
        replace: true
      });
    } else if (
      context === CONTEXTS.PLATFORM
    ) {
      navigate('/platform-admin', {
        replace: true
      });
    }
  };

  // Handle team selection - switch to team context AND change active team
  const handleTeamSelect = async teamId => {
    if (!onTeamChange || teamSwitching || selecting) return;
    setSelecting(true);
    try {
      await onTeamChange(teamId);
      setCurrentContext(CONTEXTS.TEAM);
      navigate('/oversikt', { replace: true });
    } catch (error) {
      toast.error(error?.message || 'Kunde inte byta lag.');
    } finally { setSelecting(false); }
  };

  const getContextColor = () => {
    return "bg-[rgb(var(--tn-bg-14181e))] border-[rgb(var(--tn-border-ffffff))]/[0.08] text-[rgb(var(--tn-text-f2f4f7))] hover:bg-[rgb(var(--tn-bg-181d24))] hover:text-[rgb(var(--tn-text-ffffff))]";
  };

  const getContextLabel = () => {
    switch (currentContext) {
      case CONTEXTS.TEAM:
        return activeTeam?.name || 'Lag';

      case CONTEXTS.ORGANIZATION:
        return (
          activeOrg?.name ||
          'Förening'
        );

      case CONTEXTS.PLATFORM:
        return 'Trainnova';

      default:
        return 'Välj';
    }
  };

  const getContextIcon = () => {
    switch (currentContext) {
      case CONTEXTS.TEAM:
        return Layers;

      case CONTEXTS.ORGANIZATION:
        return Building2;

      case CONTEXTS.PLATFORM:
        return LayoutDashboard;

      default:
        return Layers;
    }
  };

  const ContextIcon =
    getContextIcon();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          disabled={teamSwitching || selecting}
          className={cn(
            'w-full min-w-0 justify-between gap-1.5 h-9 min-h-[36px] py-0 px-2 rounded-[8px] border shadow-none',
            getContextColor()
          )}
        >
          <div className="flex flex-1 items-center gap-1.5 min-w-0">

            {/* Show team logo in team context, otherwise show icon */}
            {currentContext ===
              CONTEXTS.TEAM &&
            activeTeam?.logo_url ? (
              <img
                src={`${API}${activeTeam.logo_url}`}
                alt=""
                className="w-[18px] h-[18px] rounded-full object-cover flex-shrink-0"
              />
            ) : currentContext ===
                CONTEXTS.TEAM &&
              activeTeam ? (
              <div className="w-[18px] h-[18px] rounded-full bg-[var(--tn-accent-fill)]/12 border border-[var(--t-accent)]/20 flex items-center justify-center flex-shrink-0">
                <span className="text-[8px] font-bold text-[var(--t-accent)]">
                  {activeTeam.name
                    ?.charAt(0)
                    ?.toUpperCase() ||
                    'L'}
                </span>
              </div>
            ) : (
              <ContextIcon className="w-3.5 h-3.5 flex-shrink-0" />
            )}

            <span
              className="min-w-0 flex-1 truncate text-left text-[12px] font-medium"
              title={getContextLabel()}
            >
              {getContextLabel()}
            </span>
          </div>

          <ChevronDown className="w-3.5 h-3.5 flex-shrink-0 opacity-50" />
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="start"
        className="w-64 max-h-[400px] overflow-y-auto border-[rgb(var(--tn-border-ffffff))]/[0.08] bg-[rgb(var(--tn-bg-14181e))] text-[rgb(var(--tn-text-f2f4f7))] shadow-xl shadow-black/25"
      >
        {/* ALL Teams - Lagväljare (only active teams) */}
        {switchableTeams.length > 0 ? (
          <>
            <DropdownMenuLabel className="text-xs text-muted-foreground flex items-center gap-2">
              <Layers className="w-3 h-3" />
              Mina lag
            </DropdownMenuLabel>

            {switchableTeams
              .map(team => (
                <DropdownMenuItem
                  key={team.id}
                  disabled={teamSwitching || selecting}
                  onClick={() =>
                    handleTeamSelect(
                      team.id
                    )
                  }
                  className={cn(
                    'cursor-pointer',
                    currentContext ===
                      CONTEXTS.TEAM &&
                      activeTeam?.id ===
                        team.id &&
                      'bg-[var(--tn-accent-fill)]/10 text-[var(--t-text-primary)]'
                  )}
                >
                  {/* Team logo or fallback */}
                  {team.logo_url ? (
                    <img
                      src={`${API}${team.logo_url}`}
                      alt=""
                      className="w-5 h-5 rounded-full object-cover mr-2 flex-shrink-0"
                    />
                  ) : (
                    <div className="w-5 h-5 rounded-full bg-[var(--tn-accent-fill)]/12 border border-[var(--t-accent)]/20 flex items-center justify-center mr-2 flex-shrink-0">
                      <span className="text-[9px] font-bold text-[var(--t-accent)]">
                        {team.name
                          ?.charAt(0)
                          ?.toUpperCase() ||
                          'L'}
                      </span>
                    </div>
                  )}

                  <div className="flex-1 min-w-0">
                    <span className="truncate block text-sm">
                      {team.name}
                    </span>

                    <span className="text-[10px] text-muted-foreground truncate block">{accountTeamRoleLabel(user, team)}</span>
                    {team.organization_name && (
                      <span className="text-[10px] text-muted-foreground truncate block">
                        {
                          team.organization_name
                        }
                      </span>
                    )}
                  </div>

                  {currentContext ===
                    CONTEXTS.TEAM &&
                    activeTeam?.id ===
                      team.id && (
                      <Check className="w-4 h-4 ml-auto text-[var(--t-accent)] flex-shrink-0" />
                    )}
                </DropdownMenuItem>
              ))}

            <DropdownMenuSeparator />
          </>
        ) : (
          <>
            <div className="px-2 py-3 space-y-2">
              <button
                type="button"
                onClick={() =>
                  navigate(
                    '/create-team'
                  )
                }
                className="w-full flex items-center justify-center gap-1.5 text-sm px-3 py-2 rounded bg-primary text-primary-foreground hover:bg-primary/90 transition font-medium"
                data-testid="create-team-btn"
              >
                <Plus className="w-4 h-4" />
                Skapa lag
              </button>

              <button
                type="button"
                onClick={
                  handleManualRefresh
                }
                disabled={refreshing}
                className="w-full text-xs px-3 py-2 rounded bg-primary/15 text-primary hover:bg-primary/25 disabled:opacity-50 transition"
                data-testid="reload-teams-btn"
              >
                {refreshing
                  ? 'Laddar...'
                  : 'Ladda om lag'}
              </button>
            </div>

            <DropdownMenuSeparator />
          </>
        )}

        {switchableTeams.length > 0 && <>
          <DropdownMenuItem disabled={teamSwitching || selecting} onClick={() => navigate('/create-team')}>
            <Plus className="mr-2 h-4 w-4" />Skapa nytt lag
          </DropdownMenuItem>
          <DropdownMenuItem disabled={teamSwitching || selecting} onClick={() => navigate('/join')}>
            <Users className="mr-2 h-4 w-4" />Anslut med lagkod
          </DropdownMenuItem>
          <DropdownMenuSeparator />
        </>}

        {/* Organization context */}
        {isOrgAdmin &&
          !isAppAdmin &&
          userOrganizations?.length >
            0 && (
            <>
              <DropdownMenuLabel className="text-xs text-muted-foreground flex items-center gap-2">
                <Building2 className="w-3 h-3" />
                Föreningar
              </DropdownMenuLabel>

              {userOrganizations.map(
                org => {
                  const isActiveOrg =
                    currentContext ===
                      CONTEXTS.ORGANIZATION &&
                    activeOrgId ===
                      org.id;

                  return (
                    <DropdownMenuItem
                      key={org.id}
                      onClick={() =>
                        handleContextSwitch(
                          CONTEXTS.ORGANIZATION,
                          org.id
                        )
                      }
                      className={cn(
                        'cursor-pointer',
                        isActiveOrg &&
                          'bg-[var(--tn-accent-fill)]/10'
                      )}
                      data-testid={`org-item-${org.id}`}
                    >
                      <Building2 className="w-4 h-4 mr-2 text-[var(--t-accent)]" />

                      <span className="truncate">
                        {org.name}
                      </span>

                      {isActiveOrg && (
                        <Check className="w-4 h-4 ml-auto text-[var(--t-accent)]" />
                      )}
                    </DropdownMenuItem>
                  );
                }
              )}

              <DropdownMenuSeparator />
            </>
          )}

        {/* Platform context */}
        {isAppAdmin && (
          <>
            <DropdownMenuLabel className="text-xs text-muted-foreground flex items-center gap-2">
              <LayoutDashboard className="w-3 h-3" />
              Plattform
            </DropdownMenuLabel>

            <DropdownMenuItem
              onClick={() =>
                handleContextSwitch(
                  CONTEXTS.PLATFORM
                )
              }
              className={cn(
                'cursor-pointer',
                currentContext ===
                  CONTEXTS.PLATFORM &&
                  'bg-[var(--tn-accent-fill)]/10'
              )}
            >
              <LayoutDashboard className="w-4 h-4 mr-2 text-[var(--t-accent)]" />

              <span>
                Trainnova Admin
              </span>

              {currentContext ===
                CONTEXTS.PLATFORM && (
                <Check className="w-4 h-4 ml-auto text-[var(--t-accent)]" />
              )}
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

// =============================================================================
// TEAM SELECTOR
// =============================================================================

const TeamSelector = ({
  teams,
  activeTeam,
  onTeamChange
}) => {
  if (
    !teams ||
    teams.length <= 1
  ) {
    return null;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="w-full justify-between h-8 px-2 text-xs"
        >
          <span className="truncate">
            {activeTeam?.name ||
              'Välj lag'}
          </span>

          <ChevronDown className="w-3 h-3 ml-1 opacity-50" />
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="start"
        className="w-48 bg-card border-border"
      >
        {teams.map(team => (
          <DropdownMenuItem
            key={team.id}
            onClick={() =>
              onTeamChange(team.id)
            }
            className={cn(
              activeTeam?.id ===
                team.id &&
                'bg-primary/10'
            )}
          >
            <span className="truncate">
              {team.name}
            </span>

            {activeTeam?.id ===
              team.id && (
              <Check className="w-4 h-4 ml-auto" />
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

// =============================================================================
// CONTEXT HEADER COMPONENT
// =============================================================================

const ContextHeader = ({
  currentContext,
  activeTeam,
  userOrganizations
}) => {
  const location = useLocation();
  const orgId = organizationIdFromPath(location.pathname);
  const activeOrg = userOrganizations?.find(org => org.id === orgId);
  const getHeaderContent = () => {
    switch (currentContext) {
      case CONTEXTS.TEAM:
        return {
          icon: Layers,
          title:
            activeTeam?.name ||
            'Lag',
          subtitle: 'Lagkontext',
          color: 'text-green-400'
        };

      case CONTEXTS.ORGANIZATION:
        return {
          icon: Building2,
          title:
            activeOrg?.name ||
            'Förening',
          subtitle:
            'Föreningskontext',
          color: 'text-blue-400'
        };

      case CONTEXTS.PLATFORM:
        return {
          icon: LayoutDashboard,
          title: 'Trainnova',
          subtitle:
            'Plattformsadmin',
          color:
            'text-purple-400'
        };

      default:
        return null;
    }
  };

  const header =
    getHeaderContent();

  if (!header) {
    return null;
  }

  const Icon = header.icon;

  return (
    <div className="flex items-center gap-3 px-3 py-3 border-b border-border bg-card/50">

      <div
        className={cn(
          'p-2 rounded-lg',
          currentContext ===
            CONTEXTS.TEAM &&
            'bg-green-500/20',
          currentContext ===
            CONTEXTS.ORGANIZATION &&
            'bg-blue-500/20',
          currentContext ===
            CONTEXTS.PLATFORM &&
            'bg-purple-500/20'
        )}
      >
        <Icon
          className={cn(
            'w-5 h-5',
            header.color
          )}
        />
      </div>

      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-[rgb(var(--tn-text-ffffff))] truncate">
          {header.title}
        </p>

        <p className="text-[10px] text-muted-foreground uppercase tracking-wide">
          {header.subtitle}
        </p>
      </div>

    </div>
  );
};

// =============================================================================
// MAIN LAYOUT COMPONENT
// =============================================================================

export function Layout({
  children
}) {
  const { t, i18n } =
    useTranslation([
      'navigation'
    ]);

  const {
    user,
    teams,
    activeTeam,
    changeActiveTeam,
    refreshTeams,
    logout,
    isCoach,
    isAdmin,
    isHeadCoach,
    currentRoleName,
    sessionVersion,
    teamSwitching,
    teamSwitchError,
    refreshUser
  } = useAuth();
  const { activeContextKey, switching: childSwitching, isParentView: isParent, clearActiveContext, leaderWorkspace, contextCount } = useParent();
  const { isInChatView } = useChatView();
  const hasOwnWorkspace = hasOwnTeamWorkspace(user, activeTeam)
    || availableTeams(teams).some(team => hasOwnTeamWorkspace(user, team));

  const { isMobile } =
    useMobileContext();

  const { registerHandler } =
    useWebSocketContext();

  const navigate =
    useNavigate();

  const location =
    useLocation();

  const [
    mobileMenuOpen,
    setMobileMenuOpen
  ] = useState(false);

  const [
    unreadPraiseCount,
    setUnreadPraiseCount
  ] = useState(0);

  const [
    unreadChatCount,
    setUnreadChatCount
  ] = useState(0);

  const [
    userOrganizations,
    setUserOrganizations
  ] = useState([]);

  const [
    userContext,
    setUserContext
  ] = useState(null);

  // Current navigation context
  const [
    currentContext,
    setCurrentContext
  ] = useState(
    CONTEXTS.TEAM
  );

  // Determine context from URL
  useEffect(() => {
    if (
      location.pathname.startsWith(
        '/platform-admin'
      )
    ) {
      setCurrentContext(
        CONTEXTS.PLATFORM
      );
    } else if (
      location.pathname.startsWith(
        '/org/'
      )
    ) {
      setCurrentContext(
        CONTEXTS.ORGANIZATION
      );
    } else {
      setCurrentContext(
        CONTEXTS.TEAM
      );
    }
  }, [location.pathname]);

  // Fetch user context
  useEffect(() => {
    const fetchUserContext =
      async () => {
        const token =
          localStorage.getItem(
            'token'
          );

        if (
          !token ||
          !user
        ) {
          return;
        }

        try {
          const res =
            await getUserContext();

          if (
            res.data?.data
          ) {
            setUserContext(
              res.data.data
            );

            if (
              res.data.data.orgs
            ) {
              setUserOrganizations(
                res.data.data.orgs.map(
                  o => ({
                    id: o.org_id,
                    name: o.name,
                    role: o.role,
                    logo_url:
                      o.logo_url
                  })
                )
              );
            }
          }
        } catch (e) {
          console.error(
            'Failed to fetch user context:',
            e
          );

          try {
            const fallbackRes =
              await fetch(
                `${API}/api/organizations/my`,
                {
                  headers: {
                    Authorization:
                      `Bearer ${token}`
                  }
                }
              );

            if (
              fallbackRes.ok
            ) {
              const data =
                await fallbackRes.json();

              setUserOrganizations(
                data || []
              );
            }
          } catch (
            fallbackError
          ) {
            // Silently fail
          }
        }
      };

    fetchUserContext();
  }, [user]);

  // Fetch unread counts
  useEffect(() => {
    const fetchUnreadCounts =
      async () => {
        try {
          const [
            praiseRes,
            chatRes
          ] =
            await Promise.all([
              getUnreadPraiseCount(),
              getTotalUnreadCount()
            ]);

          setUnreadPraiseCount(
            praiseRes.data
              ?.count || 0
          );

          setUnreadChatCount(
            chatRes.data
              ?.total || 0
          );
        } catch (e) {
          // Silently fail
        }
      };

    if (user) {
      fetchUnreadCounts();

      const interval =
        setInterval(
          fetchUnreadCounts,
          30000
        );

      return () =>
        clearInterval(
          interval
        );
    }
  }, [user]);

  // Reset unread counts (praise only)
  useEffect(() => {
    if (
      location.pathname ===
      '/praise'
    ) {
      const timeout =
        setTimeout(
          () =>
            setUnreadPraiseCount(
              0
            ),
          1000
        );

      return () =>
        clearTimeout(
          timeout
        );
    }
  }, [location.pathname]);

  // Real-time chat badge
  useEffect(() => {
    if (!user) return;

    const unregister =
      registerHandler(
        'layout-chat-badge',
        {
          onMessage: (
            _channelId,
            message
          ) => {
            if (
              message
                ?.sender_user_id !==
                user.id &&
              location.pathname !==
                '/chat'
            ) {
              setUnreadChatCount(
                prev =>
                  prev + 1
              );
            }
          }
        }
      );

    return unregister;
  }, [
    user,
    registerHandler,
    location.pathname
  ]);

  const handleLogout =
    async () => {
      try {
        await logout();

        navigate('/login');

        toast.success(
          'Du har loggats ut'
        );
      } catch (error) {
        toast.error(
          'Kunde inte logga ut'
        );
      }
    };

  const getInitials =
    name => {
      if (!name) {
        return '?';
      }

      return name
        .split(' ')
        .map(n => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
    };

  // Permission checks
  const isAppAdmin =
    user?.global_role ===
      'app_admin' ||
    userContext
      ?.is_platform_admin;

  const isOrgAdmin =
    userContext
      ?.is_club_admin ||
    isCoach ||
    isAppAdmin;

  // Get modules context
  const modulesContext =
    useModules();

  const { isModuleEnabled } =
    modulesContext || {
      isModuleEnabled: () => true
    };

  // Filter menu items
  const filterItemsByRole =
    items => {
      return items.filter(
        item => {
          if (item.participantOnly && isCoach) return false;
          if (
            item.coachOnly &&
            !isCoach
          ) {
            return false;
          }

          if (
            item.moduleKey &&
            !isModuleEnabled(
              item.moduleKey
            ) && !(item.to === '/matches' && isModuleEnabled('tactic'))
          ) {
            return false;
          }

          return true;
        }
      );
    };

  // Resolve all club menu links from the route currently being viewed.
  const resolveOrgRoutes = sections =>
    organizationMenuForPath(sections, location.pathname);

  // Get current menu
  const getCurrentMenu =
    () => {
      switch (
        currentContext
      ) {
        case CONTEXTS.TEAM:
          return isParent &&
            !isCoach
            ? parentTeamMenuSections
            : teamMenuSections;

        case CONTEXTS.ORGANIZATION:
          return resolveOrgRoutes(
            organizationMenuSections
          );

        case CONTEXTS.PLATFORM:
          return platformMenuSections;

        default:
          return isParent &&
            !isCoach
            ? parentTeamMenuSections
            : teamMenuSections;
      }
    };

  // Add badges
  const getItemBadge =
    to => {
      if (
        to === '/praise'
      ) {
        return unreadPraiseCount;
      }

      if (
        to === '/chat'
      ) {
        return unreadChatCount;
      }

      return 0;
    };

  // Render menu sections
  const renderMenu = () => {
    const sections =
      getCurrentMenu();

    return sections.map(
      section => {
        if (
          section.coachOnly &&
          currentContext ===
            CONTEXTS.TEAM &&
          !isCoach
        ) {
          return null;
        }

        const filteredItems =
          filterItemsByRole(
            section.items
          );

        if (
          filteredItems.length ===
          0
        ) {
          return null;
        }

        return (
          <div
            key={
              section.id
            }
          >
            {section.label && (
              <SectionHeader
                label={t(
                  section.label
                )}
              />
            )}

            <div className="space-y-[2px]">
              {filteredItems.map(
                item => (
                  <NavItem
                    key={
                      item.to
                    }
                    {...item}
                    label={item.clubLabel ? item.clubLabel[(i18n.language || 'sv').startsWith('en') ? 1 : 0] : t(item.label)}
                    badge={getItemBadge(
                      item.to
                    )}
                    end={
                      item.to === '/platform-admin' ||
                      item.to ===
                        '/oversikt' ||
                      item.to.endsWith(
                        '/overview'
                      )
                    }
                  />
                )
              )}
            </div>
          </div>
        );
      }
    );
  };

  // Remount team/child pages after a confirmed context change, so local page
  // state and late component-level requests cannot leak into the next context.
  const workspace = teamSwitching || childSwitching ? (
    <div className="p-6 text-sm text-muted-foreground" role="status">Byter lag eller barn…</div>
  ) : teamSwitchError ? (
    <div className="p-6 space-y-3" role="alert">
      <p className="text-sm">{teamSwitchError}</p>
      <Button variant="outline" onClick={async () => {
        try { await refreshUser(); }
        catch { toast.error('Kontot kunde inte läsas in. Försök igen när anslutningen fungerar.'); }
      }}>Läs in kontot igen</Button>
    </div>
  ) : (
    <React.Fragment key={`${user?.id || 'guest'}:${sessionVersion}:${activeTeam?.id || 'none'}:${activeContextKey || 'none'}`}>
      {children}
    </React.Fragment>
  );

  // ===========================================================================
  // MOBILE
  // ===========================================================================

  if (isMobile) {
    const path =
      location.pathname;

    const isMatchLive =
      /^\/match\/[^/]+\/live/.test(
        path
      );

    const isChatConversation = isInChatView && /^\/chat(?:\/|$)/.test(path);
    const showAppHeader =
      !isMatchLive && !isChatConversation;

    const hideBottomNav =
      isExerciseDetailRoute(
        path
      );

    return (
      <div
        className="min-h-screen bg-background"
        style={{
          paddingTop:
            (showAppHeader || isChatConversation)
              ? 0
              : 'env(safe-area-inset-top)',

          paddingBottom:
            isChatConversation ? 0 : hideBottomNav
              ? 'env(safe-area-inset-bottom)'
              : 'calc(64px + env(safe-area-inset-bottom))',

          minHeight:
            '100dvh'
        }}
      >
        {showAppHeader && (
          <MobileAppHeader />
        )}

        <SupportModeBanner />

        <main>
          {workspace}
        </main>

        <MobileBottomNav />
      </div>
    );
  }

  // ===========================================================================
  // DESKTOP
  // ===========================================================================

  return (
    <NavigationContext.Provider
      value={{
        currentContext,
        setCurrentContext
      }}
    >
      <div className="flex h-screen bg-[rgb(var(--tn-bg-0d0f13))] overflow-hidden">

        {/* =========================================================
            SIDEBAR
            Desktop navigation; compact dimensions are in display-mode.css.
            ========================================================= */}
        <aside
          className="tn-desktop-sidebar w-[232px] flex flex-col h-full flex-shrink-0 rounded-t-[18px] overflow-hidden"
          style={{
            backgroundColor: "rgb(var(--tn-bg-10141a))",
            borderRight: "1px solid rgb(var(--tn-border-ffffff) / 0.07)"
          }}
        >
          {/* Trainova brand — use the real product logo from /public */}
          <div
            className="tn-desktop-brand flex h-[66px] items-center px-4"
            style={{
              borderBottom: "1px solid rgb(var(--tn-border-ffffff) / 0.045)"
            }}
          >
            <img
              src="/trainnova-logo-cropped.png"
              alt="Trainova"
              className="h-[44px] w-auto max-w-[168px] flex-shrink-0 object-contain object-left"
              draggable="false"
              onError={(event) => {
                event.currentTarget.onerror = null;
                event.currentTarget.src = '/trainnova-logo.png';
              }}
            />
          </div>

          {/* Team / organization selector */}
          <div
            className="px-2.5 pt-0 pb-2.5"
            style={{
              borderBottom: "1px solid rgb(var(--tn-border-ffffff) / 0.06)"
            }}
          >
            {hasOwnWorkspace ? (
              <ContextSwitcher
                currentContext={
                  currentContext
                }
                setCurrentContext={
                  setCurrentContext
                }
                activeTeam={
                  activeTeam
                }
                allTeams={
                  teams
                }
                onTeamChange={async (team) => {
                  const confirmed = await changeActiveTeam(team);
                  clearActiveContext();
                  return confirmed;
                }}
                onRefreshTeams={
                  refreshTeams
                }
                userOrganizations={
                  userOrganizations
                }
                isOrgAdmin={
                  isOrgAdmin
                }
                isAppAdmin={
                  isAppAdmin
                }
                isCoach={
                  isCoach
                }
                isMobile={
                  isMobile
                }
              />
            ) : (
              /* A parent-only account uses the child/team context as its
                 primary workspace selector. Selecting a child also switches
                 to that child's team through ParentContext. */
              <div className="flex justify-start">
                <ChildSwitcher />
              </div>
            )}
            {hasOwnWorkspace && !leaderWorkspace && (contextCount > 0 || isParent) && <div className="mt-2"><ChildSwitcher /></div>}
          </div>

          {/* Navigation */}
          <nav className="flex-1 min-h-0 overflow-y-auto py-2 px-2 space-y-1 scrollbar-thin scrollbar-thumb-border scrollbar-track-transparent">
            {renderMenu()}
          </nav>

          {/* Footer */}
          <div
            className="p-3"
            style={{
              borderTop: "1px solid rgb(var(--tn-border-ffffff) / 0.06)"
            }}
          >
            <div className="flex items-center gap-2 mb-2">

              <Avatar className="h-8 w-8 border border-[var(--t-accent)]/30">

                {user?.avatar_url && (
                  <AvatarImage
                    src={`${API}${user.avatar_url}`}
                    alt={
                      user?.name
                    }
                    className="object-cover"
                  />
                )}

                <AvatarFallback className="bg-[var(--tn-accent-fill)]/20 text-[var(--t-accent)] text-xs font-bold">
                  {getInitials(
                    user?.name
                  )}
                </AvatarFallback>
              </Avatar>

              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-[var(--t-text-primary)] truncate">
                  {user?.name}
                </p>

                <p className="text-[10px] text-[var(--t-text-secondary)] truncate">
                  {currentContext ===
                    CONTEXTS.TEAM &&
                    currentRoleName}

                  {currentContext ===
                    CONTEXTS.ORGANIZATION &&
                    t(
                      'navigation:role_labels.org_admin'
                    )}

                  {currentContext ===
                    CONTEXTS.PLATFORM &&
                    t(
                      'navigation:role_labels.platform_admin'
                    )}
                </p>
              </div>
            </div>

            <div className="flex gap-1">
              <NotificationBell />

              <Button
                variant="ghost"
                size="sm"
                className="flex-1 justify-start h-8 rounded-lg text-[13px] font-medium text-[rgb(var(--tn-text-b8c0ca))] hover:bg-[rgb(var(--tn-bg-171b21))] hover:text-[rgb(var(--tn-text-f2f4f7))]"
                onClick={() =>
                  navigate(
                    '/settings'
                  )
                }
              >
                <Settings className="w-3.5 h-3.5 mr-1.5" />

                {t(
                  'navigation:actions.settings'
                )}
              </Button>

              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                onClick={
                  handleLogout
                }
                data-testid="logout-btn"
              >
                <LogOut className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
        </aside>

        {/* =========================================================
            MAIN DESKTOP WORKSPACE

            GLOBAL TRAINNOVA STANDARD

            Sidebar:
              220px

            Normal workspace gap:
              16px desktop
              20px XL
              24px 2XL

            Max workspace:
              1880px

            This removes the old 1400px centered "island".
            Individual pages may still use narrower internal cards/forms.
            ========================================================= */}
        <div className="flex-1 min-w-0 flex flex-col overflow-hidden bg-[rgb(var(--tn-bg-0d0f13))]">

          <SupportModeBanner />

          {/* V2.5: Trial warning banner */}
          {currentContext ===
            CONTEXTS.TEAM && (
            <TrialWarningBanner />
          )}

          <main
            className={`flex-1 min-w-0 overflow-auto ${
              /^(?:\/tactics\/board|\/training\/exercises\/(?:new|[^/]+\/edit)|\/platform-admin\/exercises\/[^/]+\/edit|\/org\/[^/]+\/exercises\/[^/]+\/edit)\/?$/.test(
                location.pathname
              )
                ? 'pl-4 pr-2 py-2'
                : 'px-4 pt-3 pb-5 xl:px-5 2xl:px-6'
            }`}
          >
            {/^(?:\/tactics\/board|\/training\/exercises\/(?:new|[^/]+\/edit)|\/platform-admin\/exercises\/[^/]+\/edit|\/org\/[^/]+\/exercises\/[^/]+\/edit)\/?$/.test(
              location.pathname
            ) ? (
              workspace
            ) : (
              <div className="tn-desktop-workspace mx-auto w-full max-w-[1880px]">
                {workspace}
              </div>
            )}
          </main>

        </div>
      </div>
    </NavigationContext.Provider>
  );
}

export default Layout;
